const initStickyColumns = () => {
    if (!('ResizeObserver' in window)) {
        return;
    }

    // A column taller than the viewport gets a negative sticky top (see .pdp__info), so it sticks by its bottom edge.
    document.querySelectorAll('.pdp__gallery, .pdp__info').forEach((column) => {
        const syncHeight = () => column.style.setProperty('--pdp-sticky-h-self', column.offsetHeight + 'px');
        new ResizeObserver(syncHeight).observe(column);
        syncHeight();
    });
};

const initGalleryVideos = (gallery) => {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let lightboxOpen = false;
    const tiles = Array.from(gallery.querySelectorAll('video[data-pdp-autoplay]')).map((video) => ({
        video,
        toggle: video.closest('.pdp-gallery__item').querySelector('[data-pdp-video-toggle]'),
        inView: false,
        paused: reducedMotion,
    }));
    if (!tiles.length) {
        return;
    }

    const render = (tile) => {
        tile.toggle.toggleAttribute('data-paused', tile.paused);
        tile.toggle.setAttribute('aria-label', tile.toggle.dataset[tile.paused ? 'labelPlay' : 'labelPause']);
    };
    const sync = (tile) => {
        if (tile.inView && !tile.paused && !lightboxOpen) {
            const playing = tile.video.play();
            if (playing) {
                playing.catch(() => {
                    tile.paused = true;
                    render(tile);
                });
            }
        } else {
            tile.video.pause();
        }
    };

    tiles.forEach((tile) => {
        render(tile);
        tile.toggle.addEventListener('click', () => {
            tile.paused = !tile.paused;
            render(tile);
            sync(tile);
        });
    });

    // Plays only while on screen, so hidden carousel slides and collapsed tiles never download or run.
    if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => entries.forEach((entry) => {
            const tile = tiles.find((t) => t.video === entry.target);
            tile.inView = entry.isIntersecting;
            sync(tile);
        }), { threshold: 0.25 });
        tiles.forEach((tile) => observer.observe(tile.video));
    } else {
        tiles.forEach((tile) => {
            tile.inView = true;
            sync(tile);
        });
    }

    gallery.addEventListener('pdp:lightbox', (e) => {
        lightboxOpen = e.detail;
        tiles.forEach(sync);
    });
};

const initGallery = () => {
    const gallery = document.querySelector('[data-pdp-gallery]');
    if (!gallery) {
        return;
    }

    const more = gallery.querySelector('[data-pdp-gallery-more]');
    if (more) {
        more.addEventListener('click', () => {
            gallery.classList.add('pdp-gallery--expanded');
            more.remove();
            const firstRevealed = gallery.querySelector('[data-pdp-extra]');
            const target = firstRevealed && (firstRevealed.matches('a') ? firstRevealed : firstRevealed.querySelector('a'));
            if (target) {
                target.focus();
            }
        });
    }

    const track = gallery.querySelector('.pdp-gallery__track');
    const index = gallery.querySelector('[data-pdp-gallery-index]');
    const prev = gallery.querySelector('[data-pdp-gallery-prev]');
    const next = gallery.querySelector('[data-pdp-gallery-next]');
    if (track && index) {
        const step = () => track.children[0].getBoundingClientRect().width + parseFloat(getComputedStyle(track).columnGap || 0);
        track.addEventListener('scroll', () => {
            const slides = track.children.length;
            // Slides are narrower than the track, so the last one can never snap to the start.
            const atEnd = track.scrollLeft >= track.scrollWidth - track.clientWidth - 2;
            index.textContent = atEnd ? slides : Math.min(slides, Math.round(track.scrollLeft / step()) + 1);
            if (prev && next) {
                prev.disabled = track.scrollLeft <= 2;
                next.disabled = atEnd;
            }
        }, { passive: true });
        // Narrow desktop windows have no touch to swipe with and the scrollbar is hidden.
        if (prev && next) {
            prev.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
            next.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));
        }
    }

    initGalleryVideos(gallery);
    import(/* webpackChunkName: "productgallery" */ './productgallery').then((module) => module.default(gallery));
};

// The configurator renders its buy button and live total client-side, so the bar waits for them and mirrors them.
const initStickyBuy = () => {
    const bar = document.querySelector('[data-pdp-sticky]');
    if (!bar) {
        return;
    }

    const configurator = document.getElementById('ProductConfigurator');
    if (!configurator) {
        const buy = document.querySelector('.pdp__buy');
        if (buy && buy.querySelector('buy-button[data-article-number]')) {
            wireStickyBuy(bar, buy, () => buy.querySelector('buy-button button'));
            initStickyAddons(bar);
        }
        return;
    }

    const tryWire = () => {
        const buy = configurator.querySelector('.pdp-config__buy');
        if (!buy || !buy.querySelector('button')) {
            return false;
        }
        const stickyButton = bar.querySelector('[data-pdp-sticky-buy]');
        const syncDisabled = () => { stickyButton.disabled = buy.classList.contains('pdp-config__buy--disabled'); };
        new MutationObserver(syncDisabled).observe(buy, { attributes: true, attributeFilter: ['class'] });
        syncDisabled();

        const total = document.getElementById('ProductConfiguratorPrice');
        const price = bar.querySelector('.pdp-sticky__price');
        if (total && price) {
            const syncPrice = () => price.replaceChildren(...Array.from(total.childNodes, (node) => node.cloneNode(true)));
            new MutationObserver(syncPrice).observe(total, { childList: true, subtree: true, characterData: true });
            syncPrice();
        }

        wireStickyBuy(bar, buy, () => buy.querySelector('button'));
        return true;
    };
    if (!tryWire()) {
        const observer = new MutationObserver(() => {
            if (tryWire()) {
                observer.disconnect();
            }
        });
        observer.observe(configurator, { childList: true, subtree: true });
    }
};

// The add-on list announces its selection, since the bar's buy button adds those add-ons too.
const initStickyAddons = (bar) => {
    const meta = bar.querySelector('[data-pdp-sticky-addons]');
    const price = bar.querySelector('.pdp-sticky__price');
    if (!meta || !price) {
        return;
    }
    const original = Array.from(price.childNodes, (node) => node.cloneNode(true));
    document.addEventListener('pdp:addons', (e) => {
        const selection = e.detail;
        meta.hidden = !selection;
        meta.textContent = selection ? selection.label : '';
        price.replaceChildren(...(selection
            ? [document.createTextNode(selection.total)]
            : original.map((node) => node.cloneNode(true))));
    });
};

const wireStickyBuy = (bar, buy, realButton) => {
    const root = document.documentElement;
    const setVisible = (visible) => {
        bar.classList.toggle('pdp-sticky--visible', visible);
        bar.setAttribute('aria-hidden', String(!visible));
        root.classList.toggle('pdp-sticky-visible', visible);
        root.style.setProperty('--pdp-sticky-h', bar.offsetHeight + 'px');
    };

    // A scroll check instead of IntersectionObserver: a jump straight past the button (anchors, restored scroll)
    // never intersects, so an observer would not report it.
    let visible = false;
    let ticking = false;
    const update = () => {
        ticking = false;
        // Shown whenever the real button is off screen, also before the visitor has scrolled down to it.
        const rect = buy.getBoundingClientRect();
        const shouldShow = rect.bottom < 0 || rect.top > window.innerHeight - bar.offsetHeight;
        if (shouldShow !== visible) {
            visible = shouldShow;
            setVisible(visible);
        }
    };
    const requestUpdate = () => {
        if (!ticking) {
            ticking = true;
            window.requestAnimationFrame(update);
        }
    };

    bar.hidden = false;
    setVisible(false);
    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate);
    update();

    // Delegates to the real buy button so quantity, pack size and stock rules stay in one place.
    bar.querySelector('[data-pdp-sticky-buy]').addEventListener('click', () => {
        const button = realButton();
        // The buy wrapper blocks repeat mouse clicks with pointer-events while busy; a programmatic click is not blocked.
        if (button && !button.closest('[class*="buy-button-container--"]')) {
            button.click();
        }
    });
};

const closedHeight = (details) => {
    const style = getComputedStyle(details);
    return details.querySelector('summary').offsetHeight
        + ['borderTopWidth', 'borderBottomWidth', 'paddingTop', 'paddingBottom'].reduce((sum, prop) => sum + parseFloat(style[prop]), 0);
};

const animateDetails = (details, open, done) => {
    const from = details.getBoundingClientRect().height;
    if (details.pdpAnimation) {
        details.pdpAnimation.cancel();
    }
    if (open) {
        details.open = true;
    }
    const to = open ? details.offsetHeight : closedHeight(details);
    details.style.overflow = 'hidden';
    const animation = details.animate({ height: [`${from}px`, `${to}px`] }, { duration: 250, easing: 'ease-out' });
    details.pdpAnimation = animation;
    details.pdpTarget = open;
    const cleanup = () => {
        details.pdpAnimation = null;
        details.style.overflow = '';
        if (done) {
            done();
        }
    };
    animation.onfinish = () => {
        if (!open) {
            details.open = false;
        }
        cleanup();
    };
    animation.oncancel = cleanup;
};

// <details> only toggles instantly, so the height is animated on summary clicks.
const initAnimatedDetails = () => {
    if (!('animate' in Element.prototype) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        return;
    }

    document.querySelectorAll('.pdp-accordion__item, .pdp-faq__item').forEach((details) => {
        details.querySelector('summary').addEventListener('click', (e) => {
            e.preventDefault();
            const open = details.pdpAnimation ? !details.pdpTarget : !details.open;
            const group = details.getAttribute('name');
            if (open && group) {
                // Opening one member of a named group closes the others instantly; detach them so they can animate.
                document.querySelectorAll(`details[name="${group}"][open]`).forEach((other) => {
                    if (other !== details) {
                        other.removeAttribute('name');
                        animateDetails(other, false, () => other.setAttribute('name', group));
                    }
                });
            }
            animateDetails(details, open);
        });
    });
};

document.addEventListener('DOMContentLoaded', () => {
    initStickyColumns();
    initGallery();
    initStickyBuy();
    initAnimatedDetails();
});
